import React from 'react';
import type { AgeGroup } from '@court-sync/shared';
import { cn } from '@/lib/utils';
import {
  CANCELLED_CALENDAR_CHIP_CLASSES,
  POSTPONED_CALENDAR_CHIP_CLASSES,
  RESCHEDULED_CALENDAR_CHIP_CLASSES,
  CAMP_CALENDAR_CHIP_CLASSES,
  PRIVATE_PROGRAM_CALENDAR_CHIP_CLASSES,
  PRIVATE_PACKAGE_CALENDAR_CHIP_CLASSES,
} from '@/utils/lessonSlotColors';

export interface AgeGroupCalendarLegendProps {
  ageGroups: readonly AgeGroup[];
  className?: string;
}

export function AgeGroupCalendarLegend({ ageGroups, className }: AgeGroupCalendarLegendProps) {
  const active = ageGroups.filter((g) => g.isActive);
  if (active.length === 0) return null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground',
        className
      )}
      aria-label="Age group colour legend"
    >
      <span className="font-medium text-foreground">Age groups</span>
      {active.map((group) => (
        <span key={group.id} className="inline-flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10"
            style={{ backgroundColor: group.calendarColor }}
            aria-hidden
          />
          {group.label}
        </span>
      ))}
    </div>
  );
}

export interface ScheduleTypeStatusLegendProps {
  className?: string;
}

/** Lesson type + status marks (not age-group colours). */
export function ScheduleTypeStatusLegend({ className }: ScheduleTypeStatusLegendProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600 dark:text-gray-400',
        className
      )}
      aria-label="Lesson status legend"
    >
      <span className="font-medium text-foreground">Lesson Status</span>
      <span className="flex items-center gap-1">
        <span className={cn('h-3 w-3 rounded', PRIVATE_PROGRAM_CALENDAR_CHIP_CLASSES)} />
        Private (program)
      </span>
      <span className="flex items-center gap-1">
        <span className={cn('h-3 w-3 rounded', PRIVATE_PACKAGE_CALENDAR_CHIP_CLASSES)} />
        Private (package)
      </span>
      <span className="flex items-center gap-1">
        <span className={cn('h-3 w-3 rounded', CAMP_CALENDAR_CHIP_CLASSES)} />
        Camp
      </span>
      <span className="flex items-center gap-1">
        <span className={cn('h-3 w-3 rounded line-through opacity-75', CANCELLED_CALENDAR_CHIP_CLASSES)} />
        Cancelled
      </span>
      <span className="flex items-center gap-1">
        <span className={cn('h-3 w-3 rounded', POSTPONED_CALENDAR_CHIP_CLASSES)} />
        Postponed
      </span>
      <span className="flex items-center gap-1">
        <span className={cn('h-3 w-3 rounded', RESCHEDULED_CALENDAR_CHIP_CLASSES)} />
        Rescheduled
      </span>
      <span className="flex items-center gap-1">
        <span className="inline-flex h-3.5 min-w-[14px] items-center justify-center rounded-sm bg-gray-700 px-0.5 text-[8px] font-bold text-white">
          M
        </span>
        Make-up student(s)
      </span>
      <span className="flex items-center gap-1">
        <span className="h-3 w-3 rounded bg-slate-400 opacity-50" />
        Past
      </span>
    </div>
  );
}
